import { Fragment, useRef, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Store
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Actions
import MixpanelActions from 'app/features/mixpanel/MixpanelActions';
// Selectors
import { selectAIChatEnabled } from 'app/store/Accounts/Accounts.selectors';
import { selectCaseAiChatEnabled } from 'app/store/Cases/Cases.selectors';
import { selectSelectedPageIds } from 'app/store/pagesToolbar/pagesToolbar.selectors';
// Mui
import { Tooltip, Popover } from '@mui/material';
// Hooks
// import useToggle from 'app/hooks/useToggle';
import useAccountStatus from 'app/hooks/useAccountStatus';
// Components
import Icon from 'app/components/Icon';
import { IconButton } from 'app/components/Mui';
// Utils
import usePagesUtilities from '../utils/usePagesUtilities';
// ToDO: Should be moved to shared dialogs
import ChatMessageForm from 'app/pages/Staff/CaseDetailsPage/AIChatPage/ChatMessageForm';
// End ToDO

const SendToAIChatButton = () => {
  const { t } = useTranslation();

  const { pathname } = useLocation();

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const aiChatEnabled = useAppSelector(selectAIChatEnabled)
  const caseAiChatEnabled = useAppSelector(selectCaseAiChatEnabled);
  const selectedPageIds = useAppSelector(selectSelectedPageIds);

  const { getPagesByIds } = usePagesUtilities();

  const { isSuspended } = useAccountStatus();

  const anchorRef = useRef<HTMLButtonElement>(null);

  const pages = useMemo(() => {
    return getPagesByIds(selectedPageIds);
  }, [selectedPageIds, getPagesByIds]);

  const [open, setOpen] = useState(false);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  // const handleCallback = () => {
  //   dispatch(MixpanelActions.handleCasePagesBulkActionClick({ action: 'aiChat', numberOfPages: pages.length }));
  // }

  const isAiChatButtonDisabled = !caseAiChatEnabled || isSuspended;
  const source = pathname.includes('workspace')
    ? 'workspace'
    : pathname.includes('search')
      ? 'search'
      : 'files'
  ;

  if ( !aiChatEnabled ) return null;
  return (
    <Fragment>
      <Tooltip title={t('buttons.sendToAiChat')}>
        <IconButton
          ref={anchorRef}
          name="Send to AI Chat"
          disabled={isAiChatButtonDisabled}
          onClick={handleToggle}
          color={open ? 'primary' : 'default'}
        ><Icon icon="send_to_ai_chat" /></IconButton>
      </Tooltip>
      <Popover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            variant: 'elevation',
            elevation: 2,
            sx: {
              width: '100%',
              maxWidth: 400,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              p: 2
            }
          }
        }}
      >
        <ChatMessageForm
          sx={{ width: '100%' }}
          pages={pages}
          source={source}
          cb={() => setOpen(false)}
        />
      </Popover>
    </Fragment>
  )
}

export default SendToAIChatButton;
